/* eslint-disable @typescript-eslint/no-unused-vars */
import { Company, CompanyMember, CompanyMembershipDirectory } from '../models/client/company'
import { translateMember, buildApiUser } from './tools'
import { ApiCompany, ApiUser, ApiUserDraft } from '../models/api'
import { apiRequest } from './apiRequest'
import { ValidationError } from '../util/errors/ValidationError'
import { ApiError } from '../util/errors/ApiError'
import { DraftUser } from '../models/client/user'
import { API_USER_PATHS } from './UserClient'
import { GateFeedColumnKey } from '../models/client/observation'

type CompanyWUsers = ApiCompany & { users: Array<ApiUser> }

const getCompany = async ({
    token,
    companyId,
}: {
    companyId: string
    token: string
}): Promise<Company> => {
    const {
        data: [apiCompany],
    } = await apiRequest<CompanyWUsers[]>({
        method: 'GET',
        path: `/companies`,
        token,
        include: ['users'],
        parameters: {
            filter: {
                id: companyId,
            },
        },
    })

    if (!apiCompany)
        throw new ApiError({
            type: 'InternalServerError',
            cause: new ValidationError({
                expectedPropertyValue: 'ApiCompany',
                propertyValue: JSON.stringify(apiCompany),
                propertyName: 'apiCompany',
                variableName: 'apiCompany',
            }),
        })

    const members: CompanyMembershipDirectory = {}
    apiCompany.users?.forEach((user) => {
        try {
            if (!user?.id)
                throw new ValidationError({
                    variableName: 'user',
                    propertyName: 'id',
                    expectedPropertyValue: 'string',
                    propertyValue: typeof user.id,
                    notes: 'companyClient.getCompany',
                })
            members[user.id] = translateMember({ user, companyId })
        } catch (e) {
            if (e instanceof ValidationError) {
                return
            }
            throw e
        }
    })

    return {
        name: apiCompany.name,
        id: companyId,
        settings: apiCompany.settings,
        members,
    }
}

const deleteCompanyMember = async (params: { token: string; memberId: string }): Promise<void> => {
    const { token, memberId } = params
    await apiRequest({
        method: 'DELETE',
        path: `${API_USER_PATHS.users}/${memberId}`,
        token,
    })
}

const editCompanyMember = async (params: {
    token: string
    companyId: string
    memberId: string
    draft: DraftUser
}): Promise<CompanyMember> => {
    const { token, draft, companyId, memberId } = params
    const payload = buildApiUser({ ...draft, companyId, id: memberId })

    const { data } = await apiRequest<ApiUser, ApiUserDraft>({
        method: 'PATCH',
        path: `${API_USER_PATHS.users}/${memberId}`,
        token,
        data: payload,
    })

    return translateMember({ companyId, user: data })
}

const addCompanyMember = async (params: {
    token: string
    companyId: string
    draft: DraftUser
}): Promise<CompanyMember> => {
    const { token, draft, companyId } = params
    const payload = {
        ...buildApiUser({ ...draft, companyId }, { isNew: true }),
        password: draft.password,
    }

    const { data } = await apiRequest<
        ApiUser,
        ApiUserDraft & { password?: string | undefined | null }
    >({
        method: 'POST',
        path: API_USER_PATHS.users,
        token,
        data: payload,
    })

    return translateMember({ companyId, user: data })
}

export const companyClient = {
    getCompany,
    deleteCompanyMember,
    editCompanyMember,
    addCompanyMember,
}
